import { defineStore } from 'pinia';
import { ref, reactive, computed } from 'vue';
import axios from 'axios';
import { useAuthStore } from '@/store/auth';
import useNotifications from '@/store/notification';
import { useRouter } from 'vue-router';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('chat');

// Helper to get fresh CSRF token
const getCSRFToken = () => {
  const csrfCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='));
  return csrfCookie ? csrfCookie.split('=')[1] : null;
};

export const useChatStore = defineStore('chat', () => {
  const authStore = useAuthStore();
  const { showNotification } = useNotifications();
  const router = useRouter();

  // State
  const chats = ref([]);              // List of all chats
  const messages = reactive({});      // Messages per chat, keyed by chat ID
  const activeChat = ref(null);       // Currently open chat ID
  const newMessage = ref('');         // Message being typed
  const lastMessage = ref('');
  const api_base_url = import.meta.env.VITE_API_BASE_URL;

  // ✅ Modal state for reviewing the initial message
  const reviewModalVisible = ref(false);

  // Unread tracking
  const unreadCounts = reactive({});  // { [chatId]: number }
  const totalUnread = computed(() =>
    Object.values(unreadCounts).reduce((sum, count) => sum + (count || 0), 0)
  );

  // ✅ polling timers (NEW)
  let chatsPollTimer = null;
  let activeChatPollTimer = null;
  let _chatsInitialized = false; // suppress sound/title on the very first fetch (baseline)
  let _activeChatLastMsgCount = 0; // tracks last known message count for active-chat sound detection

  // ── New-message notification (sound + title) ───────────────────────────
  const _playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Two-tone pleasant "ding" using overlapping sine waves
      const playTone = (freq, startTime, duration, vol) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(vol, startTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      playTone(1046.5, ctx.currentTime,        0.6, 0.15);  // C6
      playTone(1318.5, ctx.currentTime + 0.08, 0.5, 0.12);  // E6 offset
      setTimeout(() => ctx.close(), 900);
    } catch (_) { /* silently ignore if audio blocked */ }
  };

  const _updateTitle = (count) => {
    const base = 'Usenlease';
    document.title = count > 0 ? `(${count}) New message${count > 1 ? 's' : ''} · ${base}` : base;
  };

  /**
   * ✅ UPDATED unread logic:
   * If backend provides msg.read, we count only messages from them with read=false.
   * Otherwise fallback to your original "after my last message" rule.
   */
  const computeUnreadForChat = (chat) => {
    if (!chat.messages || !Array.isArray(chat.messages) || !authStore.user) {
      return 0;
    }

    const myId = authStore.user.id;
    const msgs = chat.messages;

    // ✅ Prefer backend read flag if present
    const hasReadFlag = msgs.some((m) => typeof m.read === 'boolean');
    if (hasReadFlag) {
      return msgs.filter((m) => m.sender !== myId && m.read === false).length;
    }

    // ---- your original logic (UNCHANGED) ----
    let lastMyIndex = -1;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].sender === myId) {
        lastMyIndex = i;
        break;
      }
    }

    if (lastMyIndex === -1) {
      return msgs.filter((m) => m.sender !== myId).length;
    }

    return msgs
      .slice(lastMyIndex + 1)
      .filter((m) => m.sender !== myId).length;
  };

  // Fetch the list of chats for the logged-in user
  const fetchChats = async () => {
    try {
      logger.debug('Fetching chats');

      const prevTotal = Object.values(unreadCounts).reduce((s, n) => s + (n || 0), 0);
      // Snapshot per-chat counts before updating so we can identify which chat got new msgs
      const prevCounts = { ...unreadCounts };

      const response = await axios.get(`${api_base_url}/api/accounts/chats/`, {
        withCredentials: true,
      });

      let didGetNewMsg = false;

      chats.value = response.data.map((chat) => {
        const otherParticipant = chat.participants.find(
          (participant) => participant.id !== authStore.user?.id
        );

        const lastMsg =
          chat.messages && chat.messages.length
            ? chat.messages[chat.messages.length - 1]
            : null;

        const lastMsgContent = lastMsg ? lastMsg.content : 'No messages yet';

        // ✅ unread updated — skip active chat to prevent flicker
        const unread = computeUnreadForChat(chat);
        if (String(chat.id) !== String(activeChat.value)) {
          const prev = unreadCounts[String(chat.id)] || 0;
          if (unread > prev) didGetNewMsg = true;
          unreadCounts[String(chat.id)] = unread;
        }

        logger.debug(`Chat ID: ${chat.id}, Last Message: ${lastMsgContent}`);

        // Get equipment data from backend (single item, not array)
        const equipment = chat.equipment || null;
        const equipmentImage = equipment?.image_url || null;

        return {
          id: String(chat.id),
          name: otherParticipant?.username || 'Unknown',
          lastMessage: lastMsgContent,
          lastMessageTime: lastMsg?.sent_at || chat.created_at,
          created_at: chat.created_at,
          participants: chat.participants,
          item_name: chat.item_name,
          itemImage: equipmentImage || chat.messages?.find(m => m.signed_image_url)?.signed_image_url || null,
          equipment: equipment,  // Single equipment, not array
          otherParticipant,
          unreadCount: unread,
        };
      });
      // Notify if new messages arrived — but NOT on the very first fetch (that's just loading baseline)
      const newTotal = Object.values(unreadCounts).reduce((s, n) => s + (n || 0), 0);
      if (_chatsInitialized && (didGetNewMsg || newTotal > prevTotal)) {
        _playBeep();
        _updateTitle(newTotal);
        // Find the first chat that has more unread than before
        const newlyActiveChat = response.data.find(r => {
          const uid = String(r.id);
          return computeUnreadForChat(r) > (prevCounts[uid] || 0);
        });
        const senderName = newlyActiveChat?.participants?.find(p => p.id !== authStore.user?.id)?.first_name || 'Someone';
        const itemName = newlyActiveChat?.equipment?.name || newlyActiveChat?.item_name || '';
        const unreadLabel = newTotal === 1 ? '1 unread message' : `${newTotal} unread messages`;
        showNotification(
          `${unreadLabel} from ${senderName}`,
          itemName ? `About: ${itemName}\nclick to read` : 'click to read',
          'info',
          0,
          () => router.push({ name: 'Chats' })
        );
      } else {
        _updateTitle(newTotal);
      }
      _chatsInitialized = true;
    } catch (error) {
      logger.error('Failed to fetch chats', error);
      showNotification('Error', 'Failed to fetch chats', 'error');
    }
  };

  // Fetch messages for a specific chat
  // ✅ Added optional options arg (does NOT affect existing calls)
  const fetchMessages = async (chatId, options = {}) => {
    const { silent = false } = options;

    // When opening a fresh chat (not a poll), reset the baseline count
    if (!silent) _activeChatLastMsgCount = 0;

    try {
      const prevCount = _activeChatLastMsgCount;

      const response = await axios.get(
        `${api_base_url}/api/accounts/chats/${chatId}/`,
        { withCredentials: true }
      );

      const rawMsgs = response.data.messages;

      // Play sound if new messages from the other person arrived while user is in the chat
      if (silent && _chatsInitialized && rawMsgs.length > prevCount) {
        const newMsgs = rawMsgs.slice(prevCount);
        const myId = authStore.user?.id;
        if (myId && newMsgs.some(m => m.sender !== myId)) {
          _playBeep();
        }
      }

      _activeChatLastMsgCount = rawMsgs.length;

      messages[chatId] = rawMsgs.map((msg) => ({
        id: msg.id,
        text: msg.content,
        sentBy: msg.sender === authStore.user?.id ? 'me' : 'them',
        sent_at: msg.sent_at,
        sender: msg.sender,
        read: msg.seen ?? msg.read ?? false,
        imageUrl: msg.signed_image_url || null,
      }));

      // Update chat equipment data from the response (single item, not array)
      const chat = chats.value.find((c) => c.id === String(chatId));
      if (chat) {
        chat.equipment = response.data.equipment || null;
        // Update itemImage from equipment
        if (chat.equipment) {
          chat.itemImage = chat.equipment.image_url || chat.itemImage;
        }
      }

      activeChat.value = String(chatId);

      // Front-end: when user opens the chat, treat as read now
      unreadCounts[chatId] = 0;
      if (chat) {
        chat.unreadCount = 0;
      }
    } catch (error) {
      logger.error('Failed to load chat messages', error);
      if (!silent) {
        showNotification(
          'Error',
          `Failed to load messages for chat ID: ${chatId}`,
          'error'
        );
      }
    }
  };

  // ✅ Optimistic helper to update chat preview without changing UI
  const updateChatPreviewLocal = (chatId, text, sent_at) => {
    const chat = chats.value.find((c) => c.id === chatId);
    if (!chat) return;
    chat.lastMessage = text;
    chat.lastMessageTime = sent_at;
  };

  // Send a new message to the active chat
  const sendMessage = async () => {
    if (!newMessage.value.trim()) return; // Don't send empty messages

    const chatId = activeChat.value;
    if (!chatId) return;

    const receiverId = getReceiverId(chatId);
    if (!receiverId) {
      showNotification('Error', 'Could not determine message recipient.', 'error');
      return;
    }

    // ✅ optimistic append (instant on sender side)
    const content = newMessage.value;
    const tempId = `tmp-${Date.now()}`;
    const nowIso = new Date().toISOString();

    if (!messages[chatId]) messages[chatId] = [];
    messages[chatId].push({
      id: tempId,
      text: content,
      sentBy: 'me',
      sent_at: nowIso,
      sender: authStore.user?.id,
      read: false,
    });

    updateChatPreviewLocal(chatId, content, nowIso);

    // clear input immediately
    newMessage.value = '';

    try {
      await axios.post(
        `${api_base_url}/api/accounts/messages/`,
        {
          content,
          chat: chatId,
          receiver: receiverId,
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        }
      );

      // Polling (1.5s) will sync the real message from the server;
      // calling fetchMessages here risks a race where the POST isn't
      // committed yet, which would wipe the optimistic message.
    } catch (error) {
      logger.error('Send message error', error?.response?.data || error);
      // Remove the optimistic message on failure
      const idx = messages[chatId]?.findIndex((m) => m.id === tempId);
      if (idx > -1) messages[chatId].splice(idx, 1);
      // Restore the input so user doesn't lose their message
      newMessage.value = content;
      showNotification(
        'Error',
        error?.response?.data?.detail || error?.response?.data?.error || 'Failed to send the message',
        'error'
      );
    }
  };

  // Open an existing chat and fetch its messages
  const openChat = (chatId) => {
    if (!messages[chatId]) {
      fetchMessages(chatId); // Fetch messages if not loaded yet
    } else {
      activeChat.value = chatId;
      // Mark unread as read locally
      unreadCounts[chatId] = 0;
      const chat = chats.value.find((c) => c.id === chatId);
      if (chat) {
        chat.unreadCount = 0;
      }
      // Optionally also mark messages as read on the front-end
      messages[chatId] = messages[chatId].map((msg) =>
        msg.sentBy === 'them' ? { ...msg, read: true } : msg
      );
    }
  };

  // Get the receiver's ID for a specific chat (used when sending messages)
  const getReceiverId = (chatId) => {
    const chat = chats.value.find((c) => c.id === chatId);
    const receiver = chat?.participants.find(
      (participant) => participant.id !== authStore.user?.id
    );

    return receiver?.id;
  };

  // Public helper to get unread count from any component
  const getUnreadCount = (chatId) => unreadCounts[chatId] || 0;

  // Optional explicit mark-as-read helper
  const markChatAsRead = (chatId) => {
    unreadCounts[chatId] = 0;
    const chat = chats.value.find((c) => c.id === chatId);
    if (chat) chat.unreadCount = 0;
    if (messages[chatId]) {
      messages[chatId] = messages[chatId].map((msg) =>
        msg.sentBy === 'them' ? { ...msg, read: true } : msg
      );
    }
  };

  // ✅ NEW: polling for “instant” incoming messages (REST fallback)
  const startChatsPolling = (intervalMs = 4000) => {
    stopChatsPolling();
    chatsPollTimer = setInterval(() => {
      fetchChats();
    }, intervalMs);
  };

  const stopChatsPolling = () => {
    if (chatsPollTimer) clearInterval(chatsPollTimer);
    chatsPollTimer = null;
  };

  // Poll active chat messages frequently so incoming shows “instantly”
  const startActiveChatPolling = (intervalMs = 1500) => {
    stopActiveChatPolling();
    activeChatPollTimer = setInterval(() => {
      if (!activeChat.value) return;
      fetchMessages(activeChat.value, { silent: true });
    }, intervalMs);
  };

  const stopActiveChatPolling = () => {
    if (activeChatPollTimer) clearInterval(activeChatPollTimer);
    activeChatPollTimer = null;
  };

  // ✅ modal controls (UNCHANGED)
  const openReviewModal = () => {
    reviewModalVisible.value = true;
  };

  const closeReviewModal = () => {
    reviewModalVisible.value = false;
  };

  // Function to clear chat state (UNCHANGED)
  const clearChatState = () => {
    chatState.value.chatId = null;
    reviewModalVisible.value = false; // ✅ close modal too
  };

  const sendMessageAndReset = async () => {
    try {
      const equipmentImageUrl = chatState.value.equipmentImage;

      await axios.post(
        `${api_base_url}/api/accounts/messages/`,
        {
          content: chatState.value.initialMessage,
          chat: chatState.value.chatId,
          receiver: chatState.value.equipmentOwner,
          item_image_url: equipmentImageUrl,
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        }
      );

      showNotification(
        'Message Sent',
        'Your message was sent successfully!',
        'success'
      );

      clearChatState();
    } catch (error) {
      showNotification(
        'Message Error',
        `Error: ${error.response?.data.detail || error.message}`,
        'error'
      );
    }
  };

  const createChat = async (equipmentOwner, equipmentDetails) => {
    const authStoreLocal = useAuthStore();

    if (authStoreLocal.isAuthenticated) {
      try {
        const response = await axios.post(
          `${api_base_url}/api/accounts/chats/`,
          {
            participants: [equipmentOwner],
            equipment_id: equipmentDetails.id,  // UPDATED: Single equipment instead of array
          },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          }
        );

        logger.info('Chat created', { chat_id: response.data.id });
        return response.data.id;
      } catch (error) {
        showNotification(
          'Error',
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Failed to open chat.',
          'error'
        );
        return null;
      }
    } else {
      showNotification(
        'Authentication Required',
        'Please log in to start a chat!',
        'error'
      );
      return null;
    }
  };

  // State to store chat details for review
  const chatState = ref({
    chatId: null,
    equipmentImage: null,
    initialMessage: '',
    equipmentOwner: null,
    equipmentDetails: null,
    item_name: '',
  });

  return {
    chats,
    chatState,
    reviewModalVisible,
    openReviewModal,
    closeReviewModal,
    messages,
    lastMessage,
    activeChat,
    newMessage,
    fetchChats,
    fetchMessages,
    sendMessage,
    sendMessageAndReset,
    clearChatState,
    openChat,
    createChat,

    // unread exposed
    unreadCounts,
    totalUnread,
    getUnreadCount,
    markChatAsRead,

    // ✅ polling exposed (NEW)
    startChatsPolling,
    stopChatsPolling,
    startActiveChatPolling,
    stopActiveChatPolling,

    // title helper (so Chats.vue can reset title when chat is opened)
    _updateTitle,
  };
});
